import { useState, useCallback } from 'react';

import {
  Button,
  SearchForm,
  SearchFormProps,
  TableContainer,
  TableHeader,
  Typography,
  LoadingOverlay,
} from '@occc/ui-design-system';
import { Box } from '@occc-app/design-system';
import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { pluralize } from '@occc-app/lib/strings';
import { PaginationInfo } from '@occc-app/hooks/usePaginatedQuery';
import { Cpg } from '@occc/graphql';

import { SubmissionGroupLocationsTable } from './submission-group-locations-table';

import { useGetSubmissionGroupLocations } from '../../../../hooks/use-get-submission-group-locations.hook';

type SubmissionGroupAddLocationsTableControllerProps = {
  cpg: Cpg;
  onSubmit: (bulkSelectedItems: Set<string>) => void;
  isUpdatingLocations?: boolean;
};

export const SubmissionGroupAddLocationsTableController = ({
  cpg,
  onSubmit,
  isUpdatingLocations = false,
}: SubmissionGroupAddLocationsTableControllerProps) => {
  const [search, setSearch] = useState('');
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | undefined>();

  const { data, loading, error, paginationProps } = useGetSubmissionGroupLocations({
    search,
    paginationInfo,
    onPageChange: setPaginationInfo,
  });

  const { bulkSelectedItems, handleBulkSelectMultiple, handleBulkDeselectAll, handleItemSelect } =
    useBulkSelect();

  const handleSearchSubmit = useCallback<SearchFormProps['onSubmit']>(({ search }) => {
    setSearch(search || '');
    setPaginationInfo(undefined); // Reset pagination when searching
  }, []);

  const handleSubmit = () => {
    onSubmit(bulkSelectedItems);
  };

  const bulkSelectedItemsCount = bulkSelectedItems.size;

  const formatBulkSelectionLabel = useCallback(
    () => `${bulkSelectedItemsCount} ${pluralize(bulkSelectedItemsCount, 'Location')} Selected`,
    [bulkSelectedItemsCount],
  );

  return (
    <Box>
      {isUpdatingLocations && <LoadingOverlay />}
      <TableContainer>
        <TableHeader
          disableTitleTypography
          actions={<SearchForm onSubmit={handleSearchSubmit} />}
          title={
            <Typography variant='h6' whiteSpace='nowrap'>
              Choose Locations to add to the Submission Group
            </Typography>
          }
          bulkSelected={bulkSelectedItemsCount}
          bulkActions={
            <Button
              color='primary'
              variant='contained'
              onClick={handleSubmit}
              disabled={isUpdatingLocations}
            >
              {`Add selected ${pluralize(bulkSelectedItemsCount, 'Location')}`}
            </Button>
          }
          formatBulkSelectionLabel={formatBulkSelectionLabel}
        />
        <SubmissionGroupLocationsTable
          cpg={cpg}
          loading={loading}
          data={data}
          error={error}
          paginationProps={paginationProps}
          paginationInfo={paginationInfo}
          onPageChange={setPaginationInfo}
          bulkSelectedItems={bulkSelectedItems}
          handleBulkSelectMultiple={handleBulkSelectMultiple}
          handleItemSelect={handleItemSelect}
          handleBulkDeselectAll={handleBulkDeselectAll}
        />
      </TableContainer>
    </Box>
  );
};
